import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import TeamPerformanceGraph from "./TeamPerformanceGraph";
import UserTimelineModal from "./UserTimeLineModal";
import { Calendar } from "lucide-react";
import { UserTimelineData } from "./challenge.types.";
import styled from "styled-components";

interface PerformanceItem {
  date: string;
  total_steps: number;
  participants: number;
}

interface TeamMember {
  user_uid: string;
  user_name: string;
  user_progress_total_value?: number;
  image_details?: {
    image_uid?: string;
  };
}

const getDateString = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

const calculateFromDate = (toDate: string): string => {
  const to = new Date(toDate);
  const from = new Date(to);
  from.setDate(from.getDate() - 14);
  return getDateString(from);
};

const DashboardContainer = styled.div`
  padding: 24px;
  background-color: var;
  min-height: 100vh;

  @media (max-width: 1024px) {
    padding: 16px;
  }

  @media (max-width: 640px) {
    padding: 12px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-color);
  margin: 0;

  @media (max-width: 640px) {
    font-size: 20px;
  }
`;

const DateSelector = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  background: transparent;
  padding: 0;
  box-shadow: none;
  border-radius: 0;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DateLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
`;

const DateInput = styled.input`
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  color: #1f2937;
  outline: none;

  &:focus {
    border-color: #094752;
  }
`;

const ApplyButton = styled.button`
  padding: 6px 20px;
  background: #094752;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;
  height: fit-content;

  &:hover {
    background: #0a5a68;
  }
`;

const DateErrorMessage = styled.div`
  padding: 8px 12px;
  background: #fef2f2;
  color: #dc2626;
  border-radius: 6px;
  font-size: 13px;
  margin-top: 8px;
  border-left: 3px solid #dc2626;
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const PerformanceContainer = styled.div`
  display: flex;
  align-items: stretch;
  height: fit-content;

  @media (max-width: 1024px) {
    flex-direction: column;
    min-height: auto;
  }

  @media (max-width: 768px) {
  }
`;

const GraphWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

// Leaderboard Card - Matches ChallengeDashboard styling
const LeaderboardCard = styled.div`
  background: var(--card-background);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  flex: 0 0 40%;
  margin-right: 10px;
  max-width: 500px;
  height: 380px;
  overflow: hidden;
  margin-left: 1%;
  margin-right: 1%;
  @media (max-width: 1024px) {
    flex: none;
    width: 90%;
    max-width: 100%;
    height: 380px;
    margin-top: 20px;
  }

  @media (max-width: 640px) {
    padding: 20px;
    height: 350px;
  }

  @media (max-width: 480px) {
    height: 350px;
    padding: 16px;
  }
`;

const LeaderboardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  flex-shrink: 0;
`;

const TrophyIcon = styled.div`
  color: #f59e0b;
  font-size: 20px;
`;

const LeaderboardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #6b7280;
`;

const NoUsers = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
  flex: 1;
`;

const ErrorContainer = styled.div`
  padding: 40px;
  text-align: center;
  color: #dc2626;
`;

// Styled Components for Leaderboard (from ChallengeDashboard)
const LeaderboardList = styled.div<{ hasCurrentUser?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  max-height: ${(props) => (props.hasCurrentUser ? "380px" : "100%")};

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }

  @media (max-width: 640px) {
    gap: 10px;
  }
`;

const LeaderboardItem = styled.div<{ clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 12px;
  border-radius: 12px;
  background: #f8fafc;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: all 0.2s ease;
  border: none;

  &:hover {
    background: #e2e8f0;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 640px) {
    padding: 12px 10px;
    gap: 10px;
  }
`;

const RankBadge = styled.div<{ rank: number }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: white;
  background: ${(props) => {
    if (props.rank === 1) return "linear-gradient(135deg, #f59e0b, #d97706)";
    if (props.rank === 2) return "linear-gradient(135deg, #9ca3af, #6b7280)";
    if (props.rank === 3) return "linear-gradient(135deg, #cd7c2f, #a16207)";
    return "linear-gradient(135deg, #6b7280, #4b5563)";
  }};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 640px) {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
`;

const UserAvatar = styled.div<{ image?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) =>
    props.image
      ? `url(${props.image})`
      : "linear-gradient(135deg, #e5e7eb, #d1d5db)"};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 640px) {
    width: 36px;
    height: 36px;
    font-size: 12px;
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0; // Allows text truncation
`;

const UserName = styled.div`
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const UserStats = styled.div`
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 640px) {
    font-size: 12px;
  }
`;

const MetricType = styled.span`
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
  text-transform: lowercase;
`;

const CurrentUserBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #3b82f6;
  color: white;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 8px;
`;

const ChallengesTeamPerformance: React.FC = () => {
  const location = useLocation();
  const { challenge, teamData, challengeGoal, challengeFrequency } =
    location.state || {};

  const [performanceData, setPerformanceData] = useState<PerformanceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userImages, setUserImages] = useState<Record<string, string>>({});

  const initialToDate = getDateString(new Date());
  const initialFromDate = calculateFromDate(initialToDate);

  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(initialToDate);
  const [dateError, setDateError] = useState<string | null>(null);

  const [selectedUser, setSelectedUser] = useState<UserTimelineData | null>(
    null
  );
  const [showUserModal, setShowUserModal] = useState(false);

  const validateDateRange = (
    from: string,
    to: string
  ): { isValid: boolean; message: string } => {
    const fromDate = new Date(from);
    const toDate = new Date(to);

    const diffTime = Math.abs(toDate.getTime() - fromDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 14) {
      return {
        isValid: false,
        message:
          "Date range cannot exceed 15 days. Please select a shorter period.",
      };
    }

    if (fromDate > toDate) {
      return {
        isValid: false,
        message: "Start date cannot be after end date.",
      };
    }

    return { isValid: true, message: "" };
  };

  const fetchUserImages = async (imageUids: string[]) => {
    if (imageUids.length === 0) return {};

    const beAddress = process.env.REACT_APP_API_URL;
    const sessionUid = localStorage.getItem("session_uid") || "";
    const userUid = localStorage.getItem("user_uid") || "";

    try {
      const url = new URL(`${beAddress}/api/get-image-by-uid`);
      imageUids.forEach((uid) =>
        url.searchParams.append("image_uid_list", uid)
      );
      url.searchParams.set("is_icon_img", "true");
      url.searchParams.set("is_feedback_image", "false");

      const res = await fetch(url.toString(), {
        headers: {
          "session-uid": sessionUid,
          "user-uid": userUid,
        },
      });

      const data = await res.json();

      if (data.status === "Success" && Array.isArray(data.data)) {
        return Object.fromEntries(
          data.data.map((item: any) => {
            if (item.default) return [item.image_uid, ""];
            return [item.image_uid, `data:image/jpeg;base64,${item.image}`];
          })
        );
      }
    } catch (err) {
      console.error("Failed to fetch images:", err);
    }
    return {};
  };

  const fetchPerformanceData = async (from: string, to: string) => {
    if (!challenge || !teamData) return;

    try {
      setLoading(true);
      setError(null);
      const beAddress = process.env.REACT_APP_API_URL;
      const userUid = localStorage.getItem("user_uid") || "";

      const queryParams = new URLSearchParams({
        from_datetime: new Date(from).toISOString(),
        to_datetime: new Date(to).toISOString(),
        challenge_uid: challenge.challenge_uid,
        group_uid: teamData.group_uid || "null",
      });

      const response = await fetch(
        `${beAddress}/api/team-performance?${queryParams}`,
        {
          headers: {
            "user-uid": userUid,
          },
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.status === "Success" && data.data) {
        const teamPerformance: PerformanceItem[] =
          data.data.challenges_performance?.[0]?.groups_performance?.[0]
            ?.team_performance || [];

        setPerformanceData(teamPerformance);

        if (teamPerformance.length === 0) {
          setError(null);
        }
      } else {
        setError(data.message || "Failed to fetch team performance");
        setPerformanceData([]);
      }
    } catch (err: any) {
      console.error("Error fetching performance data:", err);
      setError(err.message || "Something went wrong");
      setPerformanceData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformanceData(fromDate, toDate);
  }, [challenge, teamData]);

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFromDate = e.target.value;
    const validation = validateDateRange(newFromDate, toDate);

    if (validation.isValid) {
      setFromDate(newFromDate);
      setDateError(null);
    } else {
      setFromDate(newFromDate);
      setDateError(validation.message);
    }
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newToDate = e.target.value;
    const validation = validateDateRange(fromDate, newToDate);

    if (validation.isValid) {
      setToDate(newToDate);
      setDateError(null);
    } else {
      setToDate(newToDate);
      setDateError(validation.message);
    }
  };

  const handleApplyDates = () => {
    const validation = validateDateRange(fromDate, toDate);

    if (validation.isValid) {
      setDateError(null);
      fetchPerformanceData(fromDate, toDate);
    } else {
      setDateError(validation.message);
    }
  };

  const leaderboardData = (teamData?.user_list || [])
    .map((user: TeamMember) => ({
      user_uid: user.user_uid,
      user_name: user.user_name,
      performance: user.user_progress_total_value || 0,
      image_uid: user.image_details?.image_uid,
    }))
    .sort((a: any, b: any) => b.performance - a.performance);

  useEffect(() => {
    const imageUids = leaderboardData
      .filter((user: any) => user.image_uid)
      .map((user: any) => user.image_uid);

    if (imageUids.length > 0) {
      fetchUserImages(imageUids).then(setUserImages);
    }
  }, [teamData]);

  if (!challenge || !teamData) {
    return <ErrorContainer>No challenge or team data available</ErrorContainer>;
  }

  const handleUserClick = (user: any) => {
    const fullUserData = teamData?.user_list?.find(
      (u: any) => u.user_uid === user.user_uid
    );
    if (!fullUserData) return;

    const challengeProgress = fullUserData.user_progress_map || {};

    const dailyProgress = Object.entries(challengeProgress)
      .map(([date, progress]: [string, any]) => {
        const d = new Date(date);
        const formattedDate = d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });

        return {
          date: formattedDate,
          value: Number(progress.value) || 0,
          status:
            progress.status === "completed"
              ? ("completed" as const)
              : ("incomplete" as const),
        };
      })
      .sort((a, b) => {
        const da = new Date(a.date.split(" ").reverse().join("-"));
        const db = new Date(b.date.split(" ").reverse().join("-"));
        return db.getTime() - da.getTime();
      });

    const activeDays = dailyProgress.length;
    const imageUid = fullUserData.image_details?.image_uid;

    const userTimelineData: UserTimelineData = {
      user_uid: fullUserData.user_uid,
      user_name: fullUserData.user_name,
      total_steps: fullUserData.user_progress_total_value || 0,
      active_days: activeDays,
      daily_progress: dailyProgress,
      image_uid: imageUid,
      image_url: userImages[imageUid] || "",
    };

    setSelectedUser(userTimelineData);
    setShowUserModal(true);
  };

  // Helper function to get metric display text
  const getMetricDisplay = () => {
    if (challenge?.metric_type === "distance") return "meters";
    if (challenge?.metric_type === "caloriesburned") return "kcal";
    return challenge?.metric_type || "steps";
  };

  return (
    <DashboardContainer>
      <HeaderSection>
        <Title>Team Performance - {teamData.group_name || "Team"}</Title>

        <DateSelector>
          <DateInputGroup>
            <DateLabel>From:</DateLabel>
            <DateInput
              type="date"
              value={fromDate}
              onChange={handleFromDateChange}
              max={toDate}
            />
          </DateInputGroup>
          <DateInputGroup>
            <DateLabel>To:</DateLabel>
            <DateInput
              type="date"
              value={toDate}
              onChange={handleToDateChange}
              max={getDateString(new Date())}
              min={fromDate}
            />
          </DateInputGroup>
          <ApplyButton onClick={handleApplyDates}>Apply</ApplyButton>
        </DateSelector>

        {dateError && <DateErrorMessage>{dateError}</DateErrorMessage>}
      </HeaderSection>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <PerformanceContainer>
        <GraphWrapper>
          <TeamPerformanceGraph
            data={performanceData}
            loading={loading}
            challengeGoal={challengeGoal}
            frequency={challengeFrequency || "daily"}
          />
        </GraphWrapper>
        {/* Leaderboard Section with ChallengeDashboard styling using styled components */}
        <LeaderboardCard>
          <LeaderboardHeader>
            <TrophyIcon>🏆</TrophyIcon>
            <LeaderboardTitle>Top Performers</LeaderboardTitle>
          </LeaderboardHeader>

          {loading ? (
            <LoadingSpinner>Loading leaderboard...</LoadingSpinner>
          ) : leaderboardData.length > 0 ? (
            <LeaderboardList>
              {leaderboardData.map((user: any, idx: number) => {
                const rank = idx + 1;
                const imageUid = user.image_uid;
                const imageBase64 = userImages[imageUid];
                const imageSrc = imageBase64
                  ? `data:image/png;base64,${imageBase64}`
                  : undefined;

                return (
                  <LeaderboardItem
                    key={user.user_uid}
                    clickable={true}
                    onClick={() => handleUserClick(user)}
                  >
                    <RankBadge rank={rank}>{rank}</RankBadge>
                    <UserAvatar image={imageSrc}>
                      {!imageSrc && (
                        <span>{user.user_name.charAt(0).toUpperCase()}</span>
                      )}
                      {imageSrc && <img src={imageSrc} alt={user.user_name} />}
                    </UserAvatar>
                    <UserInfo>
                      <UserName>{user.user_name}</UserName>
                      <UserStats>
                        <span>{user.performance.toLocaleString()}</span>
                        <MetricType>{getMetricDisplay()}</MetricType>
                      </UserStats>
                    </UserInfo>
                  </LeaderboardItem>
                );
              })}
            </LeaderboardList>
          ) : (
            <NoUsers>No users in the team</NoUsers>
          )}
        </LeaderboardCard>
      </PerformanceContainer>

      {showUserModal && selectedUser && (
        <UserTimelineModal
          user={selectedUser}
          metricType={challenge?.metric_type || "steps"}
          challengeUnit={challenge?.challenge_unit || "count"}
          onClose={() => setShowUserModal(false)}
        />
      )}
    </DashboardContainer>
  );
};

export default ChallengesTeamPerformance;
